// Recovery-email attribution: a ?from=email link (/subscription or the / create page) carries the email's
// identity (seq/step/var/cmp/tpl). It is kept for the tab session so it survives the
// login redirect and any navigation, sent to create-checkout-session (→ Stripe
// metadata), and cleared after a successful checkout so later purchases stay organic.

export const EMAIL_ATTRIBUTION_KEY = 'vv_email_attribution';
// Offer id from an offer email (&offer=…), kept beside the attribution; the server owns price + deadline.
export const EMAIL_OFFER_KEY = 'vv_email_offer';
export const EMAIL_OFFER_IDS = ['email_699', 'email_499', 'email_199'] as const;
export type EmailOfferId = (typeof EMAIL_OFFER_IDS)[number];

export function isEmailOfferId(value: unknown): value is EmailOfferId {
  return typeof value === 'string' && (EMAIL_OFFER_IDS as readonly string[]).includes(value);
}

export interface EmailAttribution {
  sequence?: string;
  step?: string;
  variant?: string;
  campaign?: string;
  template?: string;
}

const URL_PARAMS: Record<keyof EmailAttribution, string> = {
  sequence: 'seq',
  step: 'step',
  variant: 'var',
  campaign: 'cmp',
  template: 'tpl',
};

/** Same rule as the backend: [A-Za-z0-9_-] only, max 64 chars; empty → undefined. */
export function sanitizeAttributionValue(value: unknown): string | undefined {
  if (typeof value !== 'string' && typeof value !== 'number') return undefined;
  const clean = String(value).replace(/[^A-Za-z0-9_-]/g, '').slice(0, 64);
  return clean || undefined;
}

function sanitizeAttribution(raw: unknown): EmailAttribution | null {
  if (!raw || typeof raw !== 'object') return null;
  const out: EmailAttribution = {};
  for (const key of Object.keys(URL_PARAMS) as (keyof EmailAttribution)[]) {
    const v = sanitizeAttributionValue((raw as Record<string, unknown>)[key]);
    if (v) out[key] = v;
  }
  return Object.keys(out).length ? out : null;
}

/**
 * Store the attribution from an email link (`?from=email&…`). A new email link
 * replaces the previous one; any other URL leaves the stored value untouched.
 */
export function captureEmailAttribution(search: string): EmailAttribution | null {
  const params = new URLSearchParams(search);
  if (params.get('from') !== 'email') return null;
  captureEmailOffer(search);
  const raw: Record<string, string | null> = {};
  for (const [key, param] of Object.entries(URL_PARAMS)) raw[key] = params.get(param);
  const attribution = sanitizeAttribution(raw);
  if (!attribution) return null;
  try {
    sessionStorage.setItem(EMAIL_ATTRIBUTION_KEY, JSON.stringify(attribution));
  } catch {
    // storage blocked (private mode) — attribution is best-effort
  }
  return attribution;
}

export function getEmailAttribution(): EmailAttribution | null {
  try {
    const stored = sessionStorage.getItem(EMAIL_ATTRIBUTION_KEY);
    return stored ? sanitizeAttribution(JSON.parse(stored)) : null;
  } catch {
    return null;
  }
}

export function clearEmailAttribution(): void {
  try {
    sessionStorage.removeItem(EMAIL_ATTRIBUTION_KEY);
    sessionStorage.removeItem(EMAIL_OFFER_KEY);
  } catch {
    // ignore
  }
}

/**
 * Store the offer id from an email link (`?from=email&offer=…`). A new email link
 * replaces (or, without a valid offer, removes) the stored one; other URLs leave it.
 */
export function captureEmailOffer(search: string): EmailOfferId | null {
  const params = new URLSearchParams(search);
  if (params.get('from') !== 'email') return null;
  const offer = params.get('offer');
  try {
    if (isEmailOfferId(offer)) sessionStorage.setItem(EMAIL_OFFER_KEY, offer);
    else sessionStorage.removeItem(EMAIL_OFFER_KEY);
  } catch {
    // storage blocked — the offer still applies on this page load
  }
  return isEmailOfferId(offer) ? offer : null;
}

export function getEmailOffer(): EmailOfferId | null {
  try {
    const stored = sessionStorage.getItem(EMAIL_OFFER_KEY);
    return isEmailOfferId(stored) ? stored : null;
  } catch {
    return null;
  }
}

/** create-checkout-session body fields (only those present). */
export function emailAttributionBody(attribution?: EmailAttribution | null): Record<string, string> {
  const a = sanitizeAttribution(attribution);
  if (!a) return {};
  const body: Record<string, string> = {};
  if (a.sequence) body.email_sequence = a.sequence;
  if (a.step) body.email_step = a.step;
  if (a.variant) body.email_variant = a.variant;
  if (a.campaign) body.email_campaign = a.campaign;
  if (a.template) body.email_template = a.template;
  return body;
}

/** Analytics properties (template is already in paywall_context as email_<tpl>). */
export function emailAttributionEventProps(attribution?: EmailAttribution | null): Record<string, string> | undefined {
  const props = emailAttributionBody(attribution);
  delete props.email_template;
  return Object.keys(props).length ? props : undefined;
}
